import { v4 as uuidv4 } from 'uuid';
import httpClient from '../../services/http-clients';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { calculateInstallmentBreakdown } from '../../utils/calculateInstallmentBreakdown';
import {
  getPaymentOptions,
  isFullPaymentOnly,
} from '../../utils/paymentOptions';

export const getCartSessionId = () => {
  let cartSessionID = localStorage.getItem('cartSessionID');
  if (!cartSessionID) {
    cartSessionID = uuidv4();
    localStorage.setItem('cartSessionID', cartSessionID);
  }
  return cartSessionID;
};

export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const userID = state?.user?.data?.userID || state.auth.user?.userID;
    const { isAuthenticated } = state.auth;
    const cartSessionID = getCartSessionId();

    if (isAuthenticated && !userID) {
      return rejectWithValue('No userID found in auth or user state');
    }

    const viewCart = () =>
      isAuthenticated
        ? // POST for authorized users
          httpClient.post(`/cart/view-cart`, { userID })
        : // GET for unauthorized users
          httpClient.get(`/cart/view-cart?cartSessionID=${cartSessionID}`);

    try {
      let response = await viewCart();

      if (!response.data?.success) {
        return rejectWithValue(
          response.data?.message || 'Failed to fetch cart'
        );
      }

      // Full-payment-only items (pre-owned deals) still on an installment plan
      // server-side: switch them to 'full' so the backend payment_breakdown
      // reflects upfront payment, then refetch once
      const itemsToSync = (response.data?.data?.cart?.items || []).filter(
        (item) => isFullPaymentOnly(item) && item.selectedPaymentPlan !== 'full'
      );
      if (itemsToSync.length > 0) {
        await Promise.allSettled(
          itemsToSync.map((item) =>
            httpClient.post('/cart/update-payment-plan', {
              productID: item.productID,
              selectedPaymentPlan: 'full',
              cartSessionID,
              ...(item.selectedVariation && {
                selectedVariation: item.selectedVariation,
              }),
            })
          )
        );
        const refreshed = await viewCart();
        if (refreshed.data?.success) response = refreshed;
      }

      const cartData = response.data?.data?.cart;
      if (!cartData?.items) return response.data;

      //  Normalize invalid selectedPaymentPlan
      const normalizedItems = cartData.items.map((item) => {
        const validPlans =
          item.paymentOptionsBreakdown?.map((opt) => opt.type) || [];
        if (!validPlans.includes(item.selectedPaymentPlan)) {
          return { ...item, selectedPaymentPlan: 'full' };
        }
        return item;
      });

      //  Rebuild the cart with normalized items
      const normalizedCart = {
        ...cartData,
        items: normalizedItems,
      };

      //  Return same shape as backend but with normalized data
      return {
        ...response.data,
        data: {
          ...response.data.data,
          cart: normalizedCart,
        },
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Clear cart
export const clearCart = createAsyncThunk(
  'cart/clearCart',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const cartSessionID = getCartSessionId();
    const userID = state?.user?.data?.userID || state.auth.user?.userID;
    const { isAuthenticated } = state.auth;

    try {
      const response = await httpClient.post('/cart/clear-cart', {
        [isAuthenticated ? 'userID' : 'cartSessionID']: isAuthenticated
          ? userID
          : cartSessionID,
      });
      if (!response.data?.success) {
        return rejectWithValue(
          response.data?.message || 'Failed to clear cart'
        );
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Retry fetch cart with delay
export const retryFetchCart = createAsyncThunk(
  'cart/retryFetchCart',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Add a 500ms delay to account for potential backend persistence delay
      await new Promise((resolve) => setTimeout(resolve, 500));
      const response = await dispatch(fetchCart()).unwrap();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to retry fetch cart');
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async (product, { getState, dispatch, rejectWithValue }) => {
    const prevCart = [...getState().cart.cart];

    //  Use product’s plan if it exists, else fallback to Redux or 'monthly'
    const selectedPaymentPlan = isFullPaymentOnly(product)
      ? 'full'
      : product.selectedPaymentPlan ||
        getState().cart.selectedPaymentPlan ||
        'monthly';

    let paymentOptions = [];
    try {
      paymentOptions = JSON.parse(product.paymentOptionsBreakdown || '[]');
    } catch (error) {}

    //  Get correct plan details
    const paymentPlanDetails = paymentOptions.find(
      (option) => option.type === selectedPaymentPlan
    ) || {
      type: selectedPaymentPlan,
      amount: product.fairAppPrice || product.productPrice,
      totalPrice: product.fairAppPrice || product.productPrice,
    };

    const normalizedProduct = {
      ...product,
      quantity: product.quantity || 1,
      paymentOptionsBreakdown: selectedPaymentPlan,
      selectedPaymentPlanDetails: {
        type: selectedPaymentPlan,
        amount: Number(
          paymentPlanDetails.amount ||
            product.fairAppPrice ||
            product.productPrice
        ),
        totalPrice:
          Number(
            paymentPlanDetails.amount ||
              product.fairAppPrice ||
              product.productPrice
          ) * (product.quantity || 1),
      },
      price: Number(
        paymentPlanDetails.amount ||
          product.fairAppPrice ||
          product.productPrice
      ),
      totalPrice:
        Number(
          paymentPlanDetails.amount ||
            product.fairAppPrice ||
            product.productPrice
        ) * (product.quantity || 1),
      selectedPaymentPlan,
    };

    // Optimistic update
    dispatch(cartSlice.actions.optimisticAdd(normalizedProduct));

    try {
      const response = await httpClient.post('/cart/add-to-cart', {
        productID: product.productID,
        quantity: normalizedProduct.quantity,
        cartSessionID: getCartSessionId(),
        selectedPaymentPlan,
        termMonths: product.termMonths,
        ...(product.selectedVariation
          ? { selectedVariation: product.selectedVariation }
          : {}),
      });

      const fetchResult = await dispatch(fetchCart()).unwrap();
      if (!fetchResult.data?.cart?.items?.length) {
        await dispatch(retryFetchCart()).unwrap();
      }
      return fetchResult;
    } catch (error) {
      dispatch(cartSlice.actions.rollback(prevCart));
      const errData = error.response?.data;
      const errType = errData?.errors?.type;
      if (errType === 'TERM_MISMATCH' || errType === 'FREQUENCY_MISMATCH') {
        return rejectWithValue({
          type: errType,
          details: errData.errors.details,
          suggestion: errData.errors.suggestion,
          cartInfo: errData.errors.cart_info,
        });
      }
      return rejectWithValue(errData?.message || 'Failed to add to cart');
    }
  }
);

// Remove item
export const removeFromCart = createAsyncThunk(
  'cart/removeFromCart',
  async (
    { productID, selectedVariation },
    { dispatch, getState, rejectWithValue }
  ) => {
    const prevCart = [...getState().cart.cart];

    // Optimistic update - remove only the specific item
    const normalizedPrevVariation = selectedVariation
      ? { ...selectedVariation, colorName: undefined }
      : undefined;

    const updatedCart = prevCart.filter((item) => {
      if (item.productID !== productID) return true;

      // If no variation, remove the item
      if (!selectedVariation && !item.selectedVariation) return false;

      // If variations don't match, keep the item
      if (selectedVariation && item.selectedVariation) {
        const normalizedItemVariation = {
          ...item.selectedVariation,
          colorName: undefined,
        };
        return (
          JSON.stringify(normalizedItemVariation) !==
          JSON.stringify(normalizedPrevVariation)
        );
      }

      return true;
    });

    dispatch(cartSlice.actions.rollback(updatedCart));

    try {
      const response = await httpClient.post('/cart/remove-from-cart', {
        productID,
        cartSessionID: getCartSessionId(),
        ...(selectedVariation ? { selectedVariation } : {}),
      });

      if (!response.data?.success) {
        dispatch(cartSlice.actions.rollback(prevCart));
        return rejectWithValue('Failed to remove item');
      }

      await new Promise((resolve) => setTimeout(resolve, 500));
      const fetchResult = await dispatch(fetchCart()).unwrap();
      return fetchResult;
    } catch (error) {
      dispatch(cartSlice.actions.rollback(prevCart));
      return rejectWithValue(error.message);
    }
  }
);

// Update item quantity
export const updateCartItem = createAsyncThunk(
  'cart/updateCartItem',
  async (
    { productID, cartSessionID, quantity, selectedVariation },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await httpClient.post('/cart/update-cart-item', {
        productID,
        cartSessionID,
        quantity,
        ...(selectedVariation ? { selectedVariation } : {}),
      });

      if (!response.data?.success) {
        return rejectWithValue(
          response.data?.message || 'Failed to update item'
        );
      }

      // Refresh the cart after update
      dispatch(fetchCart());

      // return response.data;
      //  Preserve selectedPaymentPlan locally even if API doesn't return it
      return {
        ...response.data,
        quantity,
        paymentPlan: existingItem.paymentPlan,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update item');
    }
  }
);

// Update cartitem(s) payment plan
export const updateCartItemPaymentPlan = createAsyncThunk(
  'cart/updateCartItemPaymentPlan',
  async (
    { productID, selectedPaymentPlan, termMonths, selectedVariation },
    { getState, dispatch, rejectWithValue }
  ) => {
    const state = getState();
    const existingItem = state.cart.cart.find(
      (item) => item.productID === productID
    );

    if (!existingItem) throw new Error('Item not found in cart');

    try {
      const payload = {
        productID,
        selectedPaymentPlan,
        cartSessionID: getCartSessionId(),
      };
      if (termMonths != null) {
        payload.termMonths = termMonths;
      }
      if (selectedVariation) {
        payload.selectedVariation = selectedVariation;
      }

      const response = await httpClient.post(
        '/cart/update-payment-plan',
        payload
      );

      if (!response.data?.success) {
        return rejectWithValue(
          response.data?.message || 'Failed to update payment plan'
        );
      }

      await dispatch(fetchCart());

      return {
        ...response.data,
        productID,
        selectedPaymentPlan,
        paymentPlan: selectedPaymentPlan,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update payment plan');
    }
  }
);

// Update cart item term months
export const updateCartItemTermMonths = createAsyncThunk(
  'cart/updateCartItemTermMonths',
  async (
    { productID, termMonths, selectedVariation },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await httpClient.post('/cart/update-payment-plan', {
        productID,
        termMonths,
        cartSessionID: getCartSessionId(),
        ...(selectedVariation ? { selectedVariation } : {}),
      });

      if (!response.data?.success) {
        return rejectWithValue(
          response.data?.message || 'Failed to update term months'
        );
      }

      await dispatch(fetchCart());
      return { ...response.data, productID, termMonths };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update term months');
    }
  }
);

// Transfer guest cart to user cart
export const transferGuestCartToUser = createAsyncThunk(
  'cart/transferGuestCartToUser',
  async (cartSessionID, { dispatch, rejectWithValue }) => {
    try {
      if (!cartSessionID) return rejectWithValue('No cart session ID found');

      const response = await httpClient.post('/cart/transfer-guest-cart', {
        cartSessionID,
      });

      if (!response.data?.success) {
        return rejectWithValue(
          response.data?.message || 'Failed to transfer cart'
        );
      }

      // Remove guest cart session ID after successful transfer
      localStorage.removeItem('cartSessionID');

      // Fetch updated cart for logged-in user
      await dispatch(fetchCart());

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// SLICE
const initialState = {
  cart: [],
  status: 'idle',
  error: null,
  selectedPaymentPlan: 'monthly',
  cart_summary: { total_items: 0, total_quantity: 0, total_amount: 0 },
  payment_breakdown: null,
  loading: false,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setSelectedPaymentPlan: (state, action) => {
      state.selectedPaymentPlan = action.payload;
    },

    setItemPaymentPlan: (state, action) => {
      const { productID, plan, paymentOptions } = action.payload;
      const item = state.cart.find((i) => i.productID === productID);
      if (!item || item.paymentPlan === plan) return;

      item.paymentPlan = plan;

      const paymentMap = {};
      for (const option of paymentOptions) {
        if (option.type) paymentMap[option.type] = option;
      }

      const option = paymentMap[plan];
      if (!option) return;

      item.paymentPlanDetails = {
        type: plan,
        amount: Number(option.amount || item.price),
        totalPrice: Number(option.amount || item.price) * (item.quantity || 1),
      };

      item.price = Number(option.amount || item.price);
      item.totalPrice = item.price * (item.quantity || 1);

      // Update summary
      state.cart_summary = {
        total_items: state.cart.length,
        total_quantity: state.cart.reduce(
          (sum, i) => sum + Number(i.quantity || 0),
          0
        ),
        total_amount: state.cart.reduce(
          (sum, i) => sum + Number(i.totalPrice || 0),
          0
        ),
      };
    },

    optimisticAdd: (state, action) => {
      const product = action.payload;
      const existing = state.cart.find(
        (item) => item.productID === product.productID
      );

      if (!existing) {
        state.cart.push({
          ...product,
          quantity: Number(product.quantity || 1),
          paymentPlan: 'monthly',
          paymentPlanDetails: product.selectedPaymentPlanDetails || {
            type: '',
            amount: Number(product.fairAppPrice || product.productPrice),
            totalPrice:
              Number(product.fairAppPrice || product.productPrice) *
              (product.quantity || 1),
          },
          price: Number(
            product.selectedPaymentPlanDetails.amount ||
              product.fairAppPrice ||
              product.productPrice
          ),
          totalPrice:
            Number(
              product.selectedPaymentPlanDetails.amount ||
                product.fairAppPrice ||
                product.productPrice
            ) * (product.quantity || 1),
        });
      }

      // Update summary
      state.cart_summary = {
        total_items: state.cart.length,
        total_quantity: state.cart.reduce(
          (sum, i) => sum + Number(i.quantity || 0),
          0
        ),
        total_amount: state.cart.reduce(
          (sum, i) => sum + Number(i.totalPrice || 0),
          0
        ),
      };
    },

    rollback: (state, action) => {
      state.cart = action.payload;
      state.cart_summary = {
        total_items: state.cart.length,
        total_quantity: state.cart.reduce(
          (sum, i) => sum + Number(i.quantity || 0),
          0
        ),
        total_amount: state.cart.reduce(
          (sum, i) => sum + Number(i.totalPrice || 0),
          0
        ),
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const cartData = action.payload.data?.cart || {};
        const serverSessionID = action.payload.data?.owner?.cartSessionID;
        if (serverSessionID) {
          localStorage.setItem('cartSessionID', serverSessionID);
        }

        if (cartData.items && cartData.items.length > 0) {
          state.cart = cartData.items.map((item) => {
            // Parse options safely
            const options = getPaymentOptions(item);

            // Normalize plan types
            const normalizedOptions = options.map((opt) =>
              String(opt?.type || '')
                .toLowerCase()
                .trim()
            );

            // Normalize the selected plan
            let validPlan = String(item.selectedPaymentPlan || '')
              .toLowerCase()
              .trim();

            //  Ensure only valid plan remains
            if (!normalizedOptions.includes(validPlan)) {
              validPlan = normalizedOptions.includes('full')
                ? 'full'
                : normalizedOptions[0] || 'full';
            }

            const normalizedPrice = Number(
              item.fairAppSalesPrice ||
                item.price ||
                item.fairAppPrice ||
                item.productPrice
            );

            return {
              ...item,
              ...(isFullPaymentOnly(item) && {
                paymentOptionsBreakdown: options,
              }),
              paymentPlan: validPlan,
              selectedPaymentPlan: validPlan,
              price: normalizedPrice,
              totalPrice: normalizedPrice * (item.quantity || 1),
            };
          });

          state.cart_summary = cartData.summary || {
            total_items: 0,
            total_quantity: 0,
            total_amount: 0,
          };

          // Store backend payment breakdown
          state.payment_breakdown = cartData.payment_breakdown || null;
        } else {
          state.cart = [];
          state.cart_summary = {
            total_items: 0,
            total_quantity: 0,
            total_amount: 0,
          };
          state.payment_breakdown = null;
        }
      })

      .addCase(fetchCart.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch cart';
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const cartData = action.payload.data?.cart || {};
        if (cartData.items && cartData.items.length > 0) {
          state.cart = cartData.items.map((item) => {
            const normalizedPrice = Number(
              item.fairAppSalesPrice ||
                item.price ||
                item.fairAppPrice ||
                item.productPrice
            );
            return {
              ...item,
              paymentPlan: item.paymentPlan,
              price: normalizedPrice,
              totalPrice: normalizedPrice * (item.quantity || 1),
            };
          });
          state.cart_summary = cartData.summary || state.cart_summary;
          state.payment_breakdown = cartData.payment_breakdown || null;
        } else {
          // Retain optimistic state
          state.cart_summary = {
            total_items: state.cart.length,
            total_quantity: state.cart.reduce(
              (sum, i) => sum + Number(i.quantity || 0),
              0
            ),
            total_amount: state.cart.reduce(
              (sum, i) => sum + Number(i.totalPrice || 0),
              0
            ),
          };
        }
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const cartData = action.payload?.data?.cart || {};
        state.cart = (cartData.items || []).map((item) => {
          const normalizedPrice = Number(
            item.fairAppSalesPrice ||
              item.price ||
              item.fairAppPrice ||
              item.productPrice
          );
          return {
            ...item,
            paymentPlan: item.paymentPlan || item.selectedPaymentPlan,
            price: normalizedPrice,
            totalPrice: normalizedPrice * (item.quantity || 1),
          };
        });
        state.cart_summary = cartData.summary || state.cart_summary;
        state.payment_breakdown = cartData.payment_breakdown || null;
      })

      .addCase(updateCartItemPaymentPlan.fulfilled, (state, action) => {
        const { productID, selectedPaymentPlan, paymentPlan } = action.payload;

        const item = state.cart.find((i) => i.productID === productID);

        if (item) {
          //  Update both fields
          item.selectedPaymentPlan = selectedPaymentPlan;
          item.paymentPlan = paymentPlan;
        }
      })
      .addCase(updateCartItemPaymentPlan.rejected, (state, action) => {
        state.error = action.payload || 'Failed to update payment plan';
      })
      .addCase(clearCart.fulfilled, (state) => {
        state.cart = [];
        state.cart_summary = {
          total_items: 0,
          total_quantity: 0,
          total_amount: 0,
        };
        state.payment_breakdown = null;
        state.status = 'succeeded';
      })
      .addCase(clearCart.rejected, (state, action) => {
        state.error = action.payload || 'Failed to clear cart';
      });
  },
});

export const {
  setSelectedPaymentPlan,
  setItemPaymentPlan,
  optimisticAdd,
  rollback,
} = cartSlice.actions;

export const getCart = (state) => state?.cart?.cart;
export const getCartSummary = (state) => state.cart.cart_summary;

export const getTotalCartQuantity = (state) => {
  const cart = Array.isArray(state.cart.cart) ? state.cart.cart : [];
  return cart?.reduce((total, item) => total + (item.quantity || 0), 0);
};

export const getTotalCartPrice = (state) =>
  state?.cart?.cart?.reduce((sum, item) => sum + (item.total || 0), 0);

export const getCurrentQuantityById = (productID) => (state) =>
  state?.cart?.cart?.find((item) => item.productID === productID)?.quantity ??
  0;
export const getSelectedPaymentPlan = (state) => state.cart.selectedPaymentPlan;

export const getExistingCartItemById = (productID) => (state) =>
  state?.cart?.cart?.find((item) => item.productID === productID) || null;

// Helper function to normalize variation (remove colorName)
const normalizeVariation = (variation) => {
  if (!variation) return variation;
  const { colorName, ...rest } = variation;
  return rest;
};

// Helper function to compare variation objects (order-independent)
const areVariationsEqual = (var1, var2) => {
  // Normalize both variations (remove colorName before comparing)
  const normalized1 = normalizeVariation(var1);
  const normalized2 = normalizeVariation(var2);

  if (!normalized1 && !normalized2) return true;
  if (!normalized1 || !normalized2) return false;

  const keys1 = Object.keys(normalized1).sort();
  const keys2 = Object.keys(normalized2).sort();

  // Different number of keys
  if (keys1.length !== keys2.length) return false;

  // Compare each key and value
  return keys1.every((key, index) => {
    return keys2[index] === key && normalized1[key] === normalized2[key];
  });
};

// Check for existing cart item by productID AND selectedVariation
export const getExistingCartItemByIdAndVariation =
  (productID, selectedVariation) => (state) => {
    const cartItems = state?.cart?.cart || [];

    return (
      cartItems.find((item) => {
        if (item.productID !== productID) return false;

        // If neither has variation, they match
        if (!item.selectedVariation && !selectedVariation) return true;

        // If only one has variation, they don't match
        if (!item.selectedVariation || !selectedVariation) return false;

        // Compare variation objects (order-independent)
        return areVariationsEqual(item.selectedVariation, selectedVariation);
      }) || null
    );
  };

export const getPaymentBreakdown = (state) => state?.cart?.payment_breakdown;

// Raw price total (no interest) from backend summary.subtotal
export const getCartRawSubtotal = (state) => {
  const subtotal = state?.cart?.cart_summary?.subtotal;
  return subtotal != null ? subtotal : null;
};

export const getDynamicCartTotal = (state) => {
  // Use backend grand_total.overall_total (includes installment + full payment items)
  const paymentBreakdown = state?.cart?.payment_breakdown;
  if (paymentBreakdown?.grand_total?.overall_total) {
    return paymentBreakdown.grand_total.overall_total;
  }

  // Fallback to frontend calculation
  const cart = state?.cart?.cart || [];
  return cart.reduce((sum, item) => {
    const planType = item?.selectedPaymentPlan || item?.paymentPlan;
    const qty = item?.quantity || 1;

    if (planType === 'full') {
      return sum + (Number(item?.price) || 0) * qty;
    }

    const breakdown = calculateInstallmentBreakdown(item);
    if (breakdown) {
      const totalPrice =
        breakdown.downPayment +
        breakdown.installmentAmount * breakdown.numberOfInstallments;
      return sum + totalPrice * qty;
    }

    return sum + (Number(item?.price) || 0) * qty;
  }, 0);
};

export default cartSlice.reducer;
